import produce from 'immer';
import { cloneDeep } from 'lodash';

import { SYSTEM_GENERATED_STATUS_NAME } from 'consts';

import initialState from './initialState';
import { ActionTypes, Actions, State, ProjectSort } from './types';

import { Actions as EntriesActions, ActionTypes as EntriesActionTypes } from '../entries/types';
import { Actions as StatusesActions, ActionTypes as StatusesActionTypes } from '../statuses/types';

import { StatusTypeAccess } from '../collaborators';
import { generateTimeZoneSelectItems } from 'helpers/projects/imports/importTimezone';
import { ProjectOwnership, ProjectStatus, ProjectType } from 'types/data/projects/constants';
import { VariableType } from 'types/data/variables/constants';

export default (
	state: State = initialState,
	action: Actions | EntriesActions | StatusesActions
): State => {
	switch (action.type) {
		case EntriesActionTypes.GET_ENTRIES: {
			const { projectId, entries, totalCount } = action.payload;

			return produce(state, draft => {
				const { entriesCount } = draft;

				entriesCount.byProjectId[projectId] = {
					count: totalCount !== undefined ? totalCount : entries.length
				};
			});
		}

		case EntriesActionTypes.GET_MORE_ENTRIES: {
			const { projectId, entries } = action.payload;

			return produce(state, draft => {
				const { entriesCount } = draft;

				if (projectId in entriesCount.byProjectId) {
					entriesCount.byProjectId[projectId].count += entries.length;
				}
			});
		}

		case EntriesActionTypes.CREATE_ENTRY: {
			return produce(state, draft => {
				const { projectId, entriesCount } = draft;

				if (projectId && projectId in entriesCount.byProjectId) {
					entriesCount.byProjectId[projectId].count++;
				}
			});
		}

		case EntriesActionTypes.DELETE_ENTRY: {
			return produce(state, draft => {
				const { projectId, entriesCount } = draft;

				if (projectId && projectId in entriesCount.byProjectId) {
					entriesCount.byProjectId[projectId].count--;
				}
			});
		}

		//////////////////////////////////////////////////////////////////////////////
		//////////////////////////////////////////////////////////////////////////////

		case ActionTypes.SET_PROJECT_ID: {
			const { projectId } = action.payload;

			return produce(state, draft => {
				if (projectId && draft.byId[projectId]) draft.isValid = true;
			});
		}

		case ActionTypes.GET_PROJECTS: {
			const { projects, username } = action.payload;

			return produce(state, draft => {
				const { byId } = draft;

				draft.projects = cloneDeep(initialState.projects);
				draft.projects.fetched = true;
				draft.tableFilters.projects.pageIndex = 0;

				const { all, byStatus, byOwnership } = draft.projects;

				draft.initialProjectIds = projects.map(project => project.projectId);

				projects.forEach(project => {
					project.projectType = ProjectType.CORE;

					byId[project.projectId] = {
						...byId[project.projectId],
						...project
					};

					all.push(project.projectId);

					byStatus[project.status].push(project.projectId);

					const ownProject = project.createdByUser === username;
					const orphanProject = !project.createdByUser;

					byOwnership[ProjectOwnership.All].push(project.projectId);

					if (ownProject) {
						byOwnership[ProjectOwnership.Own].push(project.projectId);
					} else if (!orphanProject) {
						byOwnership[ProjectOwnership.SharedWithMe].push(project.projectId);
					}
				});
			});
		}

		case ActionTypes.GET_PROJECT: {
			const { project, username } = action.payload;

			return produce(state, draft => {
				const { byId, projects } = draft;

				draft.isValid = true;

				byId[project.projectId] = {
					...byId[project.projectId],
					...project
				};

				if (!projects.all.includes(project.projectId)) {
					projects.all.push(project.projectId);
				}

				if (!projects.byStatus[project.status].includes(project.projectId)) {
					projects.byStatus[project.status].push(project.projectId);
				}

				if (!projects.byOwnership[ProjectOwnership.All].includes(project.projectId)) {
					projects.byOwnership[ProjectOwnership.All].push(project.projectId);

					const ownProject = project.createdByUser === username;
					const orphanProject = !project.createdByUser;

					if (ownProject) {
						projects.byOwnership[ProjectOwnership.Own].push(project.projectId);
					} else if (!orphanProject) {
						projects.byOwnership[ProjectOwnership.SharedWithMe].push(project.projectId);
					}
				}
			});
		}

		case ActionTypes.SET_TABLE_FILTERS: {
			const { options } = action.payload;

			return produce(state, draft => {
				draft.tableFilters.projects = options;
			});
		}

		case ActionTypes.GET_IMPORT_DATA_TEXT: {
			const { importDataText } = action.payload;

			return produce(state, draft => {
				draft.import.helpText = importDataText;
			});
		}

		case ActionTypes.CREATE_PROJECT: {
			const { project } = action.payload;

			return produce(state, draft => {
				const { projectId, status } = project;

				const {
					byId,
					projects: { all, byStatus, byOwnership },
					initialProjectIds
				} = draft;

				byId[projectId] = project;

				if (!byStatus[status]) byStatus[status] = [];

				all.push(projectId);
				byStatus[project.status].push(projectId);
				byOwnership[ProjectOwnership.Own].push(projectId);
				byOwnership[ProjectOwnership.All].push(projectId);
				initialProjectIds.push(projectId);
			});
		}

		case ActionTypes.UPDATE_PROJECT: {
			const { project } = action.payload;

			return produce(state, draft => {
				const { byId, projects } = draft;

				const previousStatus = byId[project.projectId]
					? byId[project.projectId].status
					: undefined;

				if (previousStatus && project.status !== previousStatus) {
					projects.byStatus[previousStatus] = projects.byStatus[previousStatus].filter(
						id => id !== project.projectId
					);
					projects.byStatus[project.status].push(project.projectId);
				}

				// APPLY CHANGES - SPREAD OPERATOR DUE TO WRONG TYPE INTERSECTION
				byId[project.projectId] = {
					...byId[project.projectId],
					...project
				};
			});
		}

		case ActionTypes.RESET_PROJECT_VALIDITY: {
			return produce(state, draft => {
				draft.isValid = false;
				draft.projectId = null;
			});
		}

		case ActionTypes.COPY_PROJECT: {
			return produce(state, draft => {
				draft.metadata.refetch = false;
			});
		}

		case ActionTypes.SET_COPY_PROJECT_ID: {
			return produce(state, draft => {
				const { copiedProjectId } = action.payload;
				draft.copiedProjectId = copiedProjectId;
			});
		}

		case ActionTypes.SET_REFETCH_PROJECT: {
			return produce(state, draft => {
				draft.metadata.refetch = true;
			});
		}

		case ActionTypes.DELETE_PROJECT: {
			const { projectId } = action.payload;

			return produce(state, draft => {
				const { projectId: currentProjectId, byId, tableFilters, projects } = draft;

				if (!(projectId in byId)) return;

				const project = byId[projectId];

				draft.projects.all = draft.projects.all.filter(id => id !== project.projectId);

				draft.projects.byStatus[project.status] = draft.projects.byStatus[
					project.status
				].filter(id => id !== project.projectId);

				draft.projects.byOwnership = {
					own: draft.projects.byOwnership.own.filter(id => id !== project.projectId),
					sharedWithMe: draft.projects.byOwnership.sharedWithMe.filter(
						id => id !== project.projectId
					),
					all: draft.projects.byOwnership.all.filter(id => id !== project.projectId),
					invitations: draft.projects.byOwnership.invitations.filter(
						id => id !== project.projectId
					)
				};

				/**
				 * CLEAR `projectId` if the deleted project was accesed before
				 * This is done due to no-cleanup after leaving a project - `projectId` does NOT switch to `null`
				 */
				if (currentProjectId === projectId) draft.projectId = null;

				delete byId[projectId];

				/**
				 * REPAIR PROJECTS TABLE PAGINATION
				 */
				const { pageIndex, pageSize } = tableFilters.projects;

				const pagesCount = Math.ceil(projects.all.length / pageSize);
				const newPageIndex = pagesCount - 1;
				const lastPageIndex = newPageIndex < 0 ? 0 : newPageIndex;

				if (pageIndex > lastPageIndex) {
					tableFilters.projects.pageIndex = lastPageIndex;
				}
			});
		}

		case ActionTypes.SET_PROJECTS_SEARCH_TERM: {
			const { term } = action.payload;

			return produce(state, draft => {
				draft.filters.projects.term = term;
			});
		}

		case ActionTypes.SET_ACTIVE_STATUS: {
			const { status } = action.payload;

			return produce(state, draft => {
				draft.filters.projects.status = status;
			});
		}

		case ActionTypes.SET_ACTIVE_OWNERSHIP: {
			const { ownership } = action.payload;

			return produce(state, draft => {
				draft.filters.projects.ownership = ownership;
			});
		}

		case ActionTypes.SET_PROJECT_SORT: {
			const { sort } = action.payload;

			return produce(state, draft => {
				draft.filters.projects.sort = sort;
			});
		}

		case ActionTypes.UPLOAD_URL: {
			const { fileId, isBinaryFile } = action.payload;

			return produce(state, draft => {
				draft.import.file.fileId = fileId;
				draft.import.isBinary = isBinaryFile;
			});
		}

		case ActionTypes.UPLOAD_FILE: {
			return produce(state, draft => {
				draft.import.file.uploaded = true;
			});
		}

		case ActionTypes.STORE_CSV_CONTENT: {
			const { content, originalContent, headerDelimiter } = action.payload;

			return produce(state, draft => {
				draft.import.headerDelimiter = headerDelimiter;
				draft.import.csv = content;
				draft.import.originalContent = originalContent;
				draft.import.isBinary = false;
			});
		}

		case ActionTypes.RESET_CSV_DATA: {
			return produce(state, draft => {
				draft.import.importPercentage = 0;
				draft.import.csv = null;
				draft.import.convertedCsvData = null;
				draft.import.suggestedVariableTypes = null;
				draft.import.dataToEntries = {
					oldFormattedVariables: [],
					columnToMatch: null,
					variableToMatch: null
				};
				draft.import.formattedVariables = [];
				draft.import.importVariableSet.columnToMatchWithMainLevel = null;
				draft.import.importVariableSet.variableToMatchOnMainLevel = null;
			});
		}

		case ActionTypes.RESET_CSV: {
			return produce(state, draft => {
				draft.import.importPercentage = 0;
				draft.import.csv = null;
				draft.import.convertedCsvData = null;
			});
		}

		case ActionTypes.RESET_PROJECTS_FILTERS: {
			return produce(state, draft => {
				draft.filters.projects.ownership = ProjectOwnership.All;
				draft.filters.projects.sort = ProjectSort.byNumber;
				draft.filters.projects.status = null;
				draft.filters.projects.term = '';
			});
		}

		case ActionTypes.LEAVE_PROJECT: {
			const { projectId } = action.payload;

			return produce(state, draft => {
				const { projectId: currentProjectId, byId, projects } = draft;

				if (projects.fetched && byId[projectId]) {
					const { status } = byId[projectId];

					projects.all = projects.all.filter(project => project !== projectId);
					projects.byOwnership.sharedWithMe = projects.byOwnership.sharedWithMe.filter(
						project => project !== projectId
					);
					projects.byOwnership.all = projects.byOwnership.all.filter(
						project => project !== projectId
					);
					projects.byStatus[status] = projects.byStatus[status].filter(
						project => project !== projectId
					);

					// HANDLE LEAVING LAST SHARED PROJECT -> SWITCH TO ALL PROJECTS VIEW
					if (!projects.byOwnership.sharedWithMe.length) {
						draft.filters.projects.ownership = ProjectOwnership.All;
					}

					/**
					 * CLEAR `projectId` if the deleted project was accesed before
					 * This is done due to no-cleanup after leaving a project - `projectId` does NOT switch to `null`
					 */
					if (currentProjectId === projectId) draft.projectId = null;

					delete byId[projectId];
				}
			});
		}

		case ActionTypes.UPDATE_IMPORT_PERCENTAGE: {
			const { importPercentage } = action.payload;

			return produce(state, draft => {
				draft.import.importPercentage = importPercentage;
			});
		}

		case ActionTypes.UPDATE_PROJECT_OWNER_PERMISSIONS: {
			const { permissions } = action.payload;

			return produce(state, draft => {
				const { projectId, byId } = draft;

				if (projectId && byId[projectId]) {
					byId[projectId].userAccess = permissions;
				}
			});
		}

		case ActionTypes.SUGGEST_VARIABLE_TYPES: {
			const {
				suggestedVariableTypes,
				dateFormats,
				dateTimeFormats,
				initialSuggestions,
				isExcelOrigin,
				timeZones,
				formattedVariables
			} = action.payload;
			let parsedFormattedVariables = formattedVariables;

			if (isExcelOrigin && formattedVariables.length > 0) {
				parsedFormattedVariables = formattedVariables.map(v => {
					if (v.type === VariableType.Date) {
						return { ...v, dateFormat: v.dateFormat ?? '%Y-%m-%d' };
					}
					return v;
				});
			}

			return produce(state, draft => {
				draft.import.suggestedVariableTypes = suggestedVariableTypes;
				draft.import.initialSuggestions = initialSuggestions;
				draft.import.dateFormats = dateFormats;
				draft.import.dateTimeFormats = dateTimeFormats;
				draft.import.isExcelOrigin = isExcelOrigin;
				draft.import.timeZones = generateTimeZoneSelectItems(timeZones);
				if (formattedVariables.length > 0) {
					draft.import.formattedVariables = parsedFormattedVariables;
				}
			});
		}

		/**
		 * IMPORTS
		 */

		case ActionTypes.SET_FORMATTED_VARIABLES: {
			const { formattedVariables } = action.payload;

			return produce(state, draft => {
				draft.import.formattedVariables = formattedVariables;
			});
		}

		case ActionTypes.SET_FORMATTED_VARIABLE: {
			const { formattedVariable } = action.payload;

			return produce(state, draft => {
				const index = draft.import.formattedVariables.findIndex(
					formattedVar => formattedVar.id === formattedVariable.id
				);

				draft.import.formattedVariables[index] = formattedVariable;
			});
		}

		case ActionTypes.SET_PREVIOUS_MAPPING: {
			const { importType } = action.payload;
			return produce(state, draft => {
				const previousMapping = draft.import.previousMapping;
				const dataToEntries = draft.import.dataToEntries;
				const importVariableSet = draft.import.importVariableSet;
				previousMapping.formattedVariables = draft.import.formattedVariables;
				previousMapping.importType = importType;

				previousMapping.columnToMatch = dataToEntries.columnToMatch;
				previousMapping.variableToMatch = dataToEntries.variableToMatch;
				previousMapping.columnToMatchWithMainLevel =
					importVariableSet.columnToMatchWithMainLevel;
				previousMapping.variableToMatchOnMainLevel =
					importVariableSet.variableToMatchOnMainLevel;
			});
		}

		case ActionTypes.SET_DATA_TO_ENTRIES: {
			const {
				dataToEntries: { oldFormattedVariables, columnToMatch, variableToMatch }
			} = action.payload;

			return produce(state, draft => {
				oldFormattedVariables &&
					(draft.import.dataToEntries.oldFormattedVariables = oldFormattedVariables);
				columnToMatch && (draft.import.dataToEntries.columnToMatch = columnToMatch);
				variableToMatch && (draft.import.dataToEntries.variableToMatch = variableToMatch);
			});
		}

		case ActionTypes.GET_PROJECTS_COLLABORATORS_AVATAR_DATA: {
			const {
				data: { byProjectId: fetchedAvatars }
			} = action.payload;

			return produce(state, draft => {
				const {
					avatars: { byProjectId }
				} = draft;

				draft.avatars.byProjectId = { ...byProjectId, ...fetchedAvatars };
			});
		}

		case ActionTypes.SET_PROJECTS_VIEW_OPTION: {
			const { viewOption } = action.payload;

			return produce(state, draft => {
				draft.metadata.viewOption = viewOption;
			});
		}

		case ActionTypes.SET_PROJECTS_TABLE_VISIBLE_COLUMNS: {
			const { columnNames } = action.payload;

			return produce(state, draft => {
				const {
					metadata: { columnSettings }
				} = draft;

				columnSettings.visible = columnNames;
			});
		}

		case ActionTypes.SET_ASSIGNED_ORGANIZATION_ID: {
			const { organizationId } = action.payload;

			return produce(state, draft => {
				draft.import.assignedOrganizationId = organizationId;
			});
		}

		/**
		 * IMPORT OF SERIES
		 */

		case ActionTypes.SET_IMPORT_VARIABLE_SET: {
			const { importVariableSet } = action.payload;

			return produce(state, draft => {
				draft.import.importVariableSet = {
					...draft.import.importVariableSet,
					...importVariableSet
				};
			});
		}

		//////////////////////////////////////////////////////////////////////////////
		//////////////////////////////////////////////////////////////////////////////

		case StatusesActionTypes.CREATE_STATUS: {
			const { status } = action.payload;

			return produce(state, draft => {
				const { projectId, byId } = draft;

				if (projectId && projectId in byId) {
					const project = byId[projectId];

					const { userAccess } = project;

					if (userAccess) {
						// INIT STRUCTURE
						if (!userAccess.statusTypeAccesses) {
							userAccess.statusTypeAccesses = [];

							const noStatus: StatusTypeAccess = {
								statusTypeVariableName: SYSTEM_GENERATED_STATUS_NAME,
								viewData: true,
								editData: true,
								setStatus: true
							};

							userAccess.statusTypeAccesses.push(noStatus);
						}

						const statusAccess: StatusTypeAccess = {
							statusTypeVariableName: status.name,
							viewData: true,
							editData: true,
							setStatus: true
						};

						userAccess.statusTypeAccesses.push(statusAccess);
					}
				}
			});
		}

		case StatusesActionTypes.CREATE_STATUSES: {
			const { statuses } = action.payload;

			return produce(state, draft => {
				const { projectId, byId } = draft;

				if (projectId && projectId in byId) {
					const project = byId[projectId];

					const { userAccess } = project;

					if (userAccess) {
						// INIT STRUCTURE
						if (!userAccess.statusTypeAccesses) {
							userAccess.statusTypeAccesses = [];

							const noStatus: StatusTypeAccess = {
								statusTypeVariableName: SYSTEM_GENERATED_STATUS_NAME,
								viewData: true,
								editData: true,
								setStatus: true
							};

							userAccess.statusTypeAccesses.push(noStatus);
						}

						statuses.forEach(status => {
							const statusAccess: StatusTypeAccess = {
								statusTypeVariableName: status.name,
								viewData: true,
								editData: true,
								setStatus: true
							};

							userAccess.statusTypeAccesses?.push(statusAccess);
						});
					}
				}
			});
		}

		case StatusesActionTypes.DELETE_STATUS: {
			const { statusName } = action.payload;

			return produce(state, draft => {
				const { projectId, byId } = draft;

				if (projectId && projectId in byId) {
					const project = byId[projectId];

					const { userAccess } = project;

					if (!(userAccess && userAccess.statusTypeAccesses)) return;

					userAccess.statusTypeAccesses = userAccess.statusTypeAccesses.filter(
						statusAccess => statusAccess.statusTypeVariableName !== statusName
					);

					const hasOneStatusAccess = userAccess.statusTypeAccesses.length === 1;

					if (hasOneStatusAccess) {
						const statusAccess = userAccess.statusTypeAccesses[0];

						const isSystemGenerated =
							statusAccess.statusTypeVariableName === SYSTEM_GENERATED_STATUS_NAME;

						if (isSystemGenerated) userAccess.statusTypeAccesses = [];
					}
				}
			});
		}

		case ActionTypes.GET_PROJECT_METADATA_DEFINITION: {
			const { metadata } = action.payload;

			return produce(state, draft => {
				draft.projectMetadataDefinition = metadata;
			});
		}

		default: {
			return state;
		}
	}
};
